import type { BillingOrdwaySubscriptionModel, BillingOrdwaySubscriptionProduct } from '@customerio/hydra-ui-api';
import { BillingSystem } from '../types';
import type { BillingPlan } from '../types';
import { OrdwayBillingPeriod } from '../ordway/plans';
import { descriptionForProduct, ProductName } from '../ordway/products';
import {
  findBaseOrdwayItem,
  findOrdwayItemByProductName,
  OrdwayChargeType,
  OrdwayProductType,
  ordwayHighlights,
  ordwayIntervalDisplayName,
  parseOrdwayFeatureGroups,
} from '../ordway/utils';
import { PlanType } from '../plans';

/** Typed view of the loose `custom_fields` bag on an Ordway subscription product. */
interface OrdwaySubscriptionProductCustomFields {
  cancellable?: boolean;
  product_type?: OrdwayProductType;
  plan_type?: PlanType;
  feature_groups?: string;
  is_free?: boolean;
}

/** Typed view of the loose `custom_fields` bag on the Ordway subscription itself. */
interface OrdwaySubscriptionCustomFields {
  is_lump_sum?: boolean;
}

/**
 * An Ordway subscription transformed into the shared {@link BillingPlan} shape, with
 * the raw wire fields preserved side-by-side (mirroring how the Ember model instance
 * exposed both its attributes and its getters).
 */
export type OrdwaySubscriptionBillingPlan = BillingOrdwaySubscriptionModel &
  BillingPlan & {
    /** Discriminant identifying this as an Ordway subscription (vs Stripe). */
    billingSystem: typeof BillingSystem.Ordway;
    /** Whether the subscription is billed as a lump sum. Ports the Ember `billing.isLumpSumPlan` (Ordway branch). */
    isLumpSum: boolean;
  };

function customFields(product: BillingOrdwaySubscriptionProduct): OrdwaySubscriptionProductCustomFields {
  return (product.custom_fields ?? {}) as OrdwaySubscriptionProductCustomFields;
}

function subscriptionCustomFields(subscription: BillingOrdwaySubscriptionModel): OrdwaySubscriptionCustomFields {
  return (subscription.custom_fields ?? {}) as OrdwaySubscriptionCustomFields;
}

// Returns any product that is associated to a known base plan.
function basePlanProduct(products: BillingOrdwaySubscriptionProduct[]): BillingOrdwaySubscriptionProduct | undefined {
  return products.find((product) => {
    const productType = customFields(product).product_type;
    return productType === OrdwayProductType.CDP || productType === OrdwayProductType.Journeys;
  });
}

function planType(products: BillingOrdwaySubscriptionProduct[]): PlanType {
  const product = findBaseOrdwayItem(products) ?? basePlanProduct(products);
  return product == null ? PlanType.Essentials : (customFields(product).plan_type ?? PlanType.Essentials);
}

function basePrice(products: BillingOrdwaySubscriptionProduct[], type: PlanType): number {
  if (type === PlanType.Startup) {
    return 0;
  }

  const recurringProducts = products.filter((product) => product.charge_type !== OrdwayChargeType.Usage);

  return recurringProducts.reduce((accumulator, product) => {
    const cents = Number.parseFloat(product.effective_price);
    return cents * 100 + accumulator;
  }, 0);
}

function name(products: BillingOrdwaySubscriptionProduct[], type: PlanType): string {
  if (type === PlanType.Startup) {
    return 'Startup';
  }

  const product = findBaseOrdwayItem(products) ?? basePlanProduct(products);
  return product?.plan_name ?? 'custom';
}

/** Transform an Ordway subscription wire model into the shared {@link BillingPlan} shape. */
// fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
export function ordwaySubscriptionToBillingPlan(
  subscription: BillingOrdwaySubscriptionModel,
): OrdwaySubscriptionBillingPlan {
  const products = subscription.plans ?? [];
  const base = findBaseOrdwayItem(products);

  const customersProduct = findOrdwayItemByProductName(products, ProductName.JourneysCustomersOverages);
  const messagesProduct = findOrdwayItemByProductName(products, ProductName.JourneysMessageOverages);
  const objectsProduct = findOrdwayItemByProductName(products, ProductName.JourneysObjectsOverages);
  const profilesProduct = findOrdwayItemByProductName(products, ProductName.JourneysProfilesOverages);
  const mauProduct = findOrdwayItemByProductName(products, ProductName.MonthlyActiveUsers);

  const type = planType(products);
  const groups = parseOrdwayFeatureGroups(base == null ? undefined : customFields(base).feature_groups);

  const includedCustomers = customersProduct?.included_units ?? 0;
  const includedMessages = messagesProduct?.included_units ?? 0;
  const includedObjects = objectsProduct?.included_units ?? 0;
  const includedProfiles = profilesProduct?.included_units ?? 0;
  const includedMau = mauProduct?.included_units ?? 0;

  const mauOverageRate = mauProduct?.list_price ?? '';
  const price = basePrice(products, type);

  return {
    ...subscription,
    billingSystem: BillingSystem.Ordway,
    basePrice: price,
    intervalDisplayName: ordwayIntervalDisplayName(base?.billing_period),
    includedCustomers,
    includedMessages,
    includedObjects,
    includedProfiles,
    includedMau,
    customerOverageRate: customersProduct?.list_price ?? '',
    messageOverageRate: messagesProduct?.list_price ?? '',
    objectOverageRate: objectsProduct?.list_price ?? '',
    profileOverageRate: profilesProduct?.list_price ?? '',
    mauOverageRate,
    canCancel: base == null ? false : (customFields(base).cancellable ?? false),
    planType: type,
    featureGroups: groups,
    name: name(products, type),
    description: base?.product_id == null ? '' : descriptionForProduct(base.product_id),
    isMauPricing: includedMau > 0 && mauOverageRate !== '',
    isPaidAnnually: base?.billing_period === OrdwayBillingPeriod.Annually,
    annualPrice: price,
    highlights: ordwayHighlights(type, groups, includedProfiles, includedMessages),
    isTrial: base == null ? false : (customFields(base).is_free ?? false),
    isLumpSum: subscriptionCustomFields(subscription).is_lump_sum ?? false,
    billingTotals: subscription.billing_totals,
  };
}
